import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  NonNullableFormBuilder,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import {
  valueEndSymbolsValidator,
  valueConsecutiveSigns
} from "@onsip/web/features/shared/components/sipAddressField/sip-address-field.component";
import { Subscription } from "rxjs";

@Component({
  template: "",
  standalone: false
})
export class ExternalSipAddressFormComponent implements OnInit, OnDestroy {
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;

  domain = "onsip.com";
  addressForm = this.fb.group({
    name: [
      "",
      [Validators.required, Validators.maxLength(60), Validators.pattern(NAME_VALIDATOR_REGEX)]
    ],
    foreignAddress: [
      "",
      [
        Validators.required,
        Validators.maxLength(255),
        Validators.minLength(3),
        this.alphanumericValidator(),
        valueEndSymbolsValidator(),
        valueConsecutiveSigns(),
        this.externalDomainRepeatValidator(),
        this.externalDomainAt()
      ]
    ],
    sipAddress: [""],
    extensions: [<Array<string>>[]]
  });

  protected unsubscriber = new Subscription();

  constructor(
    protected addressSummaryService: AddressSummaryService,
    protected translate: TranslateService,
    protected fb: NonNullableFormBuilder
  ) {}

  ngOnInit(): void {
    this.initDomain();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  get extensions(): FormControl {
    return this.addressForm.get("extensions") as FormControl;
  }

  get foreignAddress(): FormControl {
    return this.addressForm.get("foreignAddress") as FormControl;
  }

  get name(): FormControl {
    return this.addressForm.get("name") as FormControl;
  }

  get sipAddress(): FormControl {
    return this.addressForm.get("sipAddress") as FormControl;
  }

  getNameError(): string {
    if (this.name.hasError("maxlength")) {
      return this.translate.instant("ONSIP_I18N.MUST_BE_LESS_THAN_60_CHARACTERS");
    }

    if (this.name.hasError("pattern")) {
      return this.translate.instant(
        "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
      );
    }

    if (this.name.hasError("required")) {
      return this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED");
    }

    return "";
  }

  getForeignAddressError(): string {
    if (this.foreignAddress.hasError("required")) {
      return this.translate.instant("ONSIP_I18N.EXTERNAL_SIP_ADDRESS_IS_REQUIRED");
    }

    if (this.foreignAddress.hasError("maxlength")) {
      return this.translate.instant("ONSIP_I18N._VALUE__MUST_BE_LESS_THAN__LENGTH__CHARACTERS", {
        value: this.translate.instant("ONSIP_I18N.EXTERNAL_ADDRESS"),
        length: 255
      });
    }

    if (this.foreignAddress.hasError("minlength")) {
      return this.translate.instant("ONSIP_I18N._VALUE__MUST_BE_MORE_THAN__LENGTH__CHARACTERS", {
        value: this.translate.instant("ONSIP_I18N.EXTERNAL_ADDRESS"),
        length: 3
      });
    }

    if (this.foreignAddress.hasError("repeatDomain")) {
      return this.translate.instant("ONSIP_I18N.CANNOT_ADD_YOUR_CURRENT_SIP_DOMAIN");
    }

    if (this.foreignAddress.hasError("atSign")) {
      return this.translate.instant("ONSIP_I18N.CORRECT_SIP_ADDRESS");
    }

    if (this.foreignAddress.hasError("wrongEnding")) {
      return this.translate.instant("ONSIP_I18N.CAN_NOT_ENDS_WITH_SYMBOLS");
    }

    if (this.foreignAddress.hasError("consecutiveSigns")) {
      return this.translate.instant("ONSIP_I18N.CANT_HAS_CONSECUTIVE___");
    }

    if (this.foreignAddress.hasError("alphanumeric")) {
      return this.translate.instant("ONSIP_I18N.CAN_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_AND___");
    }

    return "";
  }

  private initDomain(): void {
    this.unsubscriber.add(
      this.addressSummaryService.orgState.subscribe(addressSummaries => {
        this.domain = addressSummaries[0]?.domain;
      })
    );
  }

  private alphanumericValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const controlDomain: string = (control.value || "").split("@")[0];
      const pattern = /^[a-zA-Z_a-zA-Z0-9-\\.]*(\\.[_a-zA-Z0-9-\\.]+)*([a-zA-Z0-9]+)$/;
      // eslint-disable-next-line no-null/no-null
      return !pattern.test(controlDomain) ? { alphanumeric: true } : null;
    };
  }

  private externalDomainRepeatValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const controlDomain: string = ((control.value || "").split("@")[1] || "")
        .trim()
        .toLowerCase();
      // eslint-disable-next-line no-null/no-null
      return controlDomain === this.domain?.toLowerCase() ? { repeatDomain: true } : null;
    };
  }

  private externalDomainAt(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      // eslint-disable-next-line no-null/no-null
      return !(control.value || "").includes("@") ? { atSign: true } : null;
    };
  }
}
